import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BookOpen, ChevronRight, Layers } from 'lucide-react'
import { useCatalogCourses, useCatalogDecks } from '../hooks/useCatalog'
import { usePublicDecksInfinite, useCopyDeck } from '../hooks/usePublic'
import { useSwipeNavBlock } from '../hooks/useSwipeNavBlock'
import { useAuthStore, selectUser, selectSetUser } from '../stores/authStore'
import CEFRBadge from '../components/shared/CEFRBadge'
import DeckCard from '../components/shared/DeckCard'
import DeckProgressBar from '../components/shared/DeckProgressBar'
import DeckStatusBadge from '../components/shared/DeckStatusBadge'
import LanguageSelector from '../components/shared/LanguageSelector'
import { DeckCardSkeleton } from '../components/shared/LoadingSkeleton'
import { getLangMeta } from '../utils/languages'
import { useLocale } from '../i18n/context'
import apiClient from '../api/client'
import type { CatalogCourse, CatalogDeck, PublicDeck, User } from '../types'
import type { CatalogSub } from '../utils/libraryAddress'
import { useStopScroll } from '../hooks/useStopScroll'
import {
  STOP_DEFAULTS, hasStopMemory, patchStopState, stopState, type CatalogListMemory,
} from '../stores/stopMemory'

type Segment = CatalogSub
const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']

/** A list sub-tab's remembered level and page this session, or its defaults. */
function listMemory(seg: Segment): CatalogListMemory {
  if (seg === 'courses') return stopState('catalogCourses')
  if (seg === 'decks') return stopState('catalogDecks')
  return STOP_DEFAULTS.catalogDecks
}

/** Whether this sub-tab was shown before in this session, so it has state to restore. */
function isReturning(seg: Segment): boolean {
  if (seg === 'courses') return hasStopMemory('catalogCourses')
  if (seg === 'decks') return hasStopMemory('catalogDecks')
  return false
}

// The sub-tab is the URL's (`?sub=`, owned by LibraryPage — ADR-106 P1), not
// this component's: it is read on every render, so the tab shown and the
// address always agree and every sub-tab can be linked to directly.
export default function CatalogPage({ sub: segment, onSubChange }: { sub: Segment; onSubChange: (sub: Segment) => void }) {
  const { tr } = useLocale()
  const cat = tr.catalog
  const lb  = tr.library
  const cs  = tr.courses
  const user    = useAuthStore(selectUser)
  const setUser = useAuthStore(selectSetUser)

  // ADR-106 P4: Decks and Courses each keep their own level filter and page
  // for the session (and Courses its language), so a sub-tab change loads the
  // new sub-tab's own state rather than resetting to defaults. Adjusted during
  // render, so no request ever goes out for one sub-tab's filter under
  // another's.
  const [level, setLevel]     = useState<string | null>(() => listMemory(segment).level)
  const [page, setPage]       = useState(() => listMemory(segment).page)
  const [language, setLanguage] = useState(() => stopState('catalogCourses').language)  // courses tab
  const [prevSegment, setPrevSegment] = useState(segment)
  const [restoring, setRestoring] = useState(() => isReturning(segment))
  const [fellBack, setFellBack]   = useState(false)
  if (segment !== prevSegment) {
    setPrevSegment(segment)
    setLevel(listMemory(segment).level)
    setPage(listMemory(segment).page)
    setRestoring(isReturning(segment))
    setFellBack(false)
  }
  useEffect(() => {
    if (segment === 'decks') patchStopState('catalogDecks', { level, page })
    else if (segment === 'courses') patchStopState('catalogCourses', { level, page, language })
  }, [segment, level, page, language])

  const decksQuery   = useCatalogDecks({ level, page, pageSize: 30, enabled: segment === 'decks' })
  const coursesQuery = useCatalogCourses({ language, level, page, pageSize: 24, enabled: segment === 'courses' })

  // Tapping the sub-tab already showing is handled one level up (it scrolls
  // that stop to the top and changes nothing else — ADR-106 Q12).
  function switchSegment(seg: Segment) {
    onSubChange(seg)
  }

  // Explore segment has no CEFR filter — hide filter row and counts when active.
  const isExplore = segment === 'explore'

  function handleLevelToggle(lvl: string) {
    setLevel((prev) => (prev === lvl ? null : lvl))
    setPage(1)
  }

  // Values for the active segment (Explore manages its own state)
  const isLoading  = segment === 'decks' ? decksQuery.isLoading  : coursesQuery.isLoading
  const isError    = segment === 'decks' ? decksQuery.isError    : coursesQuery.isError
  const total      = segment === 'decks' ? (decksQuery.data?.total ?? 0) : (coursesQuery.data?.total ?? 0)
  const pageSize   = segment === 'decks' ? 30 : 24
  const totalPages = Math.ceil(total / pageSize) || 1

  const availLevels = segment === 'decks'
    ? (decksQuery.data?.available_levels ?? CEFR_LEVELS)
    : (coursesQuery.data?.available_levels ?? CEFR_LEVELS)

  const availLangs = coursesQuery.data?.available_languages ?? ['en']

  // ── ADR-106 P4: restore, checked against the server (Decks and Courses) ──
  // A returning visit's level, page and language each fall back on their own
  // if the server no longer offers them (owner decision for P4); if any did,
  // the list is not the one the remembered offset belonged to, so the stop
  // opens at the top instead of restoring it.
  const listQuery   = segment === 'decks' ? decksQuery : coursesQuery
  const listData    = segment === 'explore' ? undefined : listQuery.data
  const levelValid  = !listData || level === null || listData.available_levels.includes(level)
  // An empty list is "nothing offered to this reader" (ADR-111 D6), not "the
  // remembered language is gone": the empty state shows, and restore settles.
  const langValid   = segment !== 'courses' || !coursesQuery.data
    || coursesQuery.data.available_languages.length === 0
    || coursesQuery.data.available_languages.includes(language)
  const pageValid   = !listData || page <= totalPages
  useEffect(() => {
    if (!restoring || segment === 'explore') return
    if (!levelValid || !langValid) {
      if (!langValid) setLanguage(STOP_DEFAULTS.catalogCourses.language)
      if (!levelValid) setLevel(null)
      setPage(1)
      setFellBack(true)
    } else if (!pageValid) {
      setPage(totalPages)
      setFellBack(true)
    }
  }, [restoring, segment, levelValid, langValid, pageValid, totalPages])
  const listReady = segment !== 'explore' && !isLoading && listData !== undefined && levelValid && langValid && pageValid
  useEffect(() => { if (listReady) setRestoring(false) }, [listReady])
  useStopScroll(
    segment === 'explore' ? null : segment === 'decks' ? 'catalog-decks' : 'catalog-courses',
    { ready: listReady, restore: !fellBack },
  )

  return (
    // No maxWidth/padding of its own — Layout already caps, centers, and
    // insets this route's page container (var(--page-max-width-narrow), the
    // same column Library's Mine tab reads through unchanged). A second
    // maxWidth here put Catalog's content a further 16px in from that
    // column at every desktop width and, below it, on a different edge than
    // the bar's tabs entirely — see LibraryPage's MineDeckContent for the
    // sibling tab obeying the same rule already.
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* ── Page header — heading is mobile-only; desktop's identity is the
          screen-header bar's "Library" (see useScreenHeader in LibraryPage),
          never both. The subtitle stays at every width — it says what
          Catalog holds, which the bar's identity never states, so it is
          doing work the bar does not. Mirrors MineDeckContent's own
          `md:hidden` heading for the sibling tab. ── */}
      <div>
        <h1 className="md:hidden" style={{ margin: '0 0 4px', fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
          {cat.heading}
        </h1>
        <p style={{ margin: 0, fontSize: 15, color: 'var(--text-secondary)' }}>
          {cat.subtitle}
        </p>
      </div>

      {/* ── Segment control ──────────────────────────────────────────────── */}
      {/* overflow-x:auto + scrollbar-none: self-scrolls on narrow viewports */}
      <div className="scrollbar-none" style={{ display: 'flex', alignItems: 'stretch', borderBottom: '1px solid var(--border)', marginBottom: -1, overflowX: 'auto' }}>
        <SegmentTab
          label={cat.decksTab}
          count={decksQuery.data?.total}
          active={segment === 'decks'}
          onClick={() => switchSegment('decks')}
        />
        <SegmentTab
          label={cat.coursesTab}
          count={coursesQuery.data?.total}
          active={segment === 'courses'}
          onClick={() => switchSegment('courses')}
        />
        <SegmentTab
          label={cat.exploreTab}
          active={segment === 'explore'}
          onClick={() => switchSegment('explore')}
        />
      </div>

      {/* Everything below the sub-tab strip — the part a swipe between Catalog
          sub-tabs moves; the heading and the strip above stay still (ADR-106
          Q6 as revised). Same column and gap as its parent, so the layout is
          unchanged. */}
      <div data-swipe-region="catalog" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* ── Filters — hidden for Explore (it manages its own search) ────── */}
      {!isExplore && <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 4 }}>
        {segment === 'courses' && (
          <LanguageSelector
            value={language}
            onChange={(lang) => { setLanguage(lang); setLevel(null); setPage(1) }}
            options={availLangs.length ? availLangs : ['en']}
          />
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {availLevels.map((lvl) => (
            <button
              key={lvl}
              onClick={() => handleLevelToggle(lvl)}
              style={{
                padding: '5px 13px',
                borderRadius: 50,
                border: `1.5px solid ${level === lvl ? 'var(--accent)' : 'var(--border)'}`,
                backgroundColor: level === lvl ? 'var(--accent-soft)' : 'var(--bg-surface)',
                color: level === lvl ? 'var(--accent)' : 'var(--text-secondary)',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: 'var(--font-ui)',
                transition: 'all 0.15s',
                letterSpacing: '0.04em',
                minHeight: 34,
              }}
            >
              {lvl}
            </button>
          ))}
          {level && (
            <button
              onClick={() => { setLevel(null); setPage(1) }}
              style={{
                padding: '5px 11px', borderRadius: 50,
                border: '1px solid var(--border)', backgroundColor: 'transparent',
                color: 'var(--text-secondary)', fontSize: 12,
                cursor: 'pointer', fontFamily: 'var(--font-ui)', minHeight: 34,
              }}
            >
              {cat.clearFilter}
            </button>
          )}
        </div>

        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {isLoading ? '…' : segment === 'decks' ? lb.deckCount(total) : cs.courseCount(total)}
        </span>
      </div>}

      {/* ── Content ──────────────────────────────────────────────────────── */}
      {isExplore ? (
        <ExploreSegment />
      ) : isError ? (
        <ErrorState />
      ) : isLoading ? (
        <DeckGrid>
          {Array.from({ length: 9 }).map((_, i) => <DeckCardSkeleton key={i} />)}
        </DeckGrid>
      ) : segment === 'decks' ? (
        <DecksSegment decks={decksQuery.data?.items ?? []} level={level} />
      ) : (
        <CoursesSegment
          courses={coursesQuery.data?.items ?? []}
          user={user}
          onSwitchExplLang={async (lang) => {
            try {
              const { data } = await apiClient.patch<User>('/api/v1/auth/me', { explanation_language: lang })
              setUser(data)
            } catch { /* silent */ }
          }}
        />
      )}

      {/* ── Pagination — hidden for Explore (uses load-more) ─────────────── */}
      {!isExplore && totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, paddingTop: 8 }}>
          <PaginationBtn disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>{cat.prev}</PaginationBtn>
          <span style={{ fontSize: 14, color: 'var(--text-secondary)', padding: '0 8px' }}>
            {page} / {totalPages}
          </span>
          <PaginationBtn disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>{cat.next}</PaginationBtn>
        </div>
      )}
      </div>
    </div>
  )
}

// ── Decks segment ─────────────────────────────────────────────────────────────

function DecksSegment({ decks, level }: { decks: CatalogDeck[]; level: string | null }) {
  const { tr } = useLocale()
  const cat = tr.catalog
  if (decks.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 0' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>📖</div>
        <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 6px' }}>
          {cat.noDecks}
        </p>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>
          {level ? cat.noLevelDecks(level) : cat.noSystemDecks}
        </p>
      </div>
    )
  }
  return (
    <DeckGrid>
      {decks.map((deck) => <CatalogSystemDeckCard key={deck.id} deck={deck} />)}
    </DeckGrid>
  )
}

// ── Courses segment ───────────────────────────────────────────────────────────

interface CoursesSegmentProps {
  courses: CatalogCourse[]
  user: User | null
  onSwitchExplLang: (lang: string) => Promise<void>
}

function CoursesSegment({ courses, user, onSwitchExplLang }: CoursesSegmentProps) {
  const { tr } = useLocale()
  const cat = tr.catalog
  if (courses.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 0' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>📚</div>
        <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 6px' }}>
          {cat.coursesComingSoon}
        </p>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: '0 0 20px', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
          {cat.coursesComingSoonDesc}
        </p>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
          {cat.coursesComingSoonHint(cat.decksTabHint)}
        </p>
      </div>
    )
  }
  return (
    <DeckGrid>
      {courses.map((course) => (
        <CatalogCourseCard
          key={course.id}
          course={course}
          userExplLang={user?.explanation_language ?? null}
          onSwitchExplLang={onSwitchExplLang}
        />
      ))}
    </DeckGrid>
  )
}

// ── System deck card (catalog — composes from shared DeckCard) ────────────────

function CatalogSystemDeckCard({ deck }: { deck: CatalogDeck }) {
  const { tr } = useLocale()
  const navigate = useNavigate()

  const metaSlot = deck.cefr_level ? (
    <>
      <span aria-hidden="true">·</span>
      <CEFRBadge level={deck.cefr_level} />
    </>
  ) : undefined

  const belowHeaderSlot = (
    <>
      {deck.description && (
        <p
          style={{
            margin: '8px 0 0',
            fontSize: 13,
            fontFamily: 'var(--font-ui)',
            color: 'var(--text-secondary)',
            lineHeight: 1.5,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {deck.description}
        </p>
      )}
      {deck.card_count > 0 && (
        <div style={{ marginTop: 12 }}>
          <DeckProgressBar learned={deck.learned_count} total={deck.card_count} width="100%" height={3} />
        </div>
      )}
    </>
  )

  return (
    <DeckCard
      deckId={deck.id}
      title={deck.title}
      cardCount={deck.card_count}
      isSystem
      category={deck.topic ?? undefined}
      hoverShadow
      onClick={() => navigate(`/decks/${deck.id}`)}
      metaSlot={metaSlot}
      belowHeaderSlot={belowHeaderSlot}
      actionLabel={tr.catalog.browseBtn}
      onAction={() => navigate(`/decks/${deck.id}`)}
      bottomLeftSlot={!deck.is_active_for_learning ? <DeckStatusBadge status="paused" size="sm" /> : undefined}
    />
  )
}

// ── Course card (unchanged from original) ─────────────────────────────────────

interface CatalogCourseCardProps {
  course: CatalogCourse
  userExplLang: string | null
  onSwitchExplLang: (lang: string) => Promise<void>
}

function CatalogCourseCard({ course, userExplLang, onSwitchExplLang }: CatalogCourseCardProps) {
  const { tr } = useLocale()
  const cat = tr.catalog
  const navigate = useNavigate()
  const [confirmSwitch, setConfirmSwitch] = useState(false)
  const [switching, setSwitching] = useState(false)
  // ADR-106 P3: a pending in-card confirmation, the same kind of surface as
  // Mine's inline delete confirmation (Decision 3.4), blocks while it is shown.
  useSwipeNavBlock(confirmSwitch, 'catalog-course-language-confirm')

  const showLangIndicator = !!userExplLang && course.language && course.language !== userExplLang
  const courseLangMeta = getLangMeta(course.language)

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/catalog/${course.id}`)}
      onKeyDown={(e) => e.key === 'Enter' && navigate(`/catalog/${course.id}`)}
      style={{
        backgroundColor: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 16,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        boxShadow: 'var(--shadow-card)',
        transition: 'transform 0.2s, box-shadow 0.2s',
        cursor: 'pointer',
        position: 'relative',
        outline: 'none',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--shadow-float)' }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'var(--shadow-card)' }}
    >
      {/* Language mismatch indicator */}
      {showLangIndicator && (
        <div style={{ position: 'relative' }}>
          {confirmSwitch ? (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                padding: '6px 10px', borderRadius: 8,
                backgroundColor: 'var(--bg-page)', border: '1px solid var(--border)', fontSize: 12,
              }}
            >
              <span style={{ color: 'var(--text-secondary)', flex: 1 }}>
                {cat.switchLangTo(courseLangMeta.label)}
              </span>
              <button
                onClick={async (e) => {
                  e.stopPropagation(); setSwitching(true)
                  await onSwitchExplLang(course.language)
                  setSwitching(false); setConfirmSwitch(false)
                }}
                disabled={switching}
                style={{ padding: '3px 10px', borderRadius: 50, border: 'none', backgroundColor: 'var(--accent-fill)', color: 'var(--text-on-accent)', fontSize: 11, fontWeight: 700, cursor: switching ? 'wait' : 'pointer' }}
              >
                {switching ? cat.switching : cat.switchBtn}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setConfirmSwitch(false) }}
                style={{ padding: '3px 8px', borderRadius: 50, border: '1px solid var(--border)', backgroundColor: 'transparent', color: 'var(--text-secondary)', fontSize: 11, cursor: 'pointer' }}
              >
                {cat.cancel}
              </button>
            </div>
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); setConfirmSwitch(true) }}
              title={`This course is in ${courseLangMeta.label}. Click to switch your explanation language.`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px',
                borderRadius: 50, border: '1px solid var(--border)', backgroundColor: 'var(--bg-page)',
                color: 'var(--text-secondary)', fontSize: 11, fontWeight: 500,
                cursor: 'pointer', fontFamily: 'var(--font-ui)', transition: 'border-color 0.15s, color 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)' }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)' }}
            >
              <span aria-hidden>{courseLangMeta.flag}</span>
              {cat.availableIn(courseLangMeta.label)}
            </button>
          )}
        </div>
      )}

      {/* Icon + title */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12, flexShrink: 0,
          backgroundColor: 'var(--accent-soft)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <BookOpen size={20} color="var(--accent)" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{
            margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)',
            lineHeight: 1.3,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {course.title}
          </h3>
          <CEFRBadge level={course.cefr_level} />
        </div>
      </div>

      {course.description && (
        <p style={{
          margin: 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {course.description}
        </p>
      )}

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <StatChip icon={<Layers size={12} />} value={course.deck_count} label={cat.decks} />
        <StatChip icon={<BookOpen size={12} />} value={course.card_count} label={cat.cards} />
        {course.topic_count > 0 && <StatChip icon={null} value={course.topic_count} label={cat.topics} />}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'auto' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 3, color: 'var(--accent)', fontSize: 13, fontWeight: 600 }}>
          {course.is_enrolled ? cat.continueBtn : cat.previewBtn}
          <ChevronRight size={14} />
        </span>
      </div>
    </div>
  )
}

// ── Shared layout helpers ─────────────────────────────────────────────────────

function DeckGrid({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {children}
    </div>
  )
}

function ErrorState() {
  const { tr } = useLocale()
  return (
    <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
      <p style={{ fontSize: 15 }}>{tr.catalog.loadError}</p>
    </div>
  )
}

function StatChip({ icon, value, label }: { icon: React.ReactNode; value: number; label: string }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-secondary)' }}>
      {icon && <span style={{ color: 'var(--text-secondary)', display: 'flex' }}>{icon}</span>}
      <b style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{value}</b>
      {label}
    </span>
  )
}

function SegmentTab({
  label, count, active, onClick,
}: {
  label: string; count?: number; active: boolean; onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '10px 20px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: 600,
        color: active ? 'var(--accent)' : 'var(--text-secondary)',
        borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
        marginBottom: -1,
        transition: 'color 0.15s, border-color 0.15s',
        fontFamily: 'var(--font-ui)',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      {label}
      {count !== undefined && (
        <span style={{ fontSize: 12, color: active ? 'var(--accent)' : 'var(--text-secondary)', opacity: 0.8 }}>
          {count}
        </span>
      )}
    </button>
  )
}

function PaginationBtn({ children, disabled, onClick }: { children: React.ReactNode; disabled: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '8px 16px', borderRadius: 50,
        border: '1px solid var(--border)',
        backgroundColor: disabled ? 'transparent' : 'var(--bg-surface)',
        color: disabled ? 'var(--text-secondary)' : 'var(--text-primary)',
        fontSize: 13, fontWeight: 500,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        fontFamily: 'var(--font-ui)',
      }}
    >
      {children}
    </button>
  )
}

// ── Explore segment — community content ───────────────────────────────────────

function ExploreSegment() {
  const { tr } = useLocale()
  const cat = tr.catalog
  const lb = tr.library
  const navigate = useNavigate()

  // ADR-106 P4: the text in the field and the search it applied are kept for
  // the session, along with how many "load more" pages were loaded — the
  // remembered offset lies within them.
  //
  // One exception (owner decision for P4): Explore applies its search by
  // itself after a pause, so text typed but not yet applied when the learner
  // left is an interrupted apply, not a state they chose. Coming back, it is
  // completed as the debounce would have done. That makes it a different list
  // from the one the offset and the page count belonged to, so neither is
  // restored: the stop opens at the top (the P4 rule for a list-defining value
  // that changed). Mine is different — it applies on submit, so its typed but
  // unsubmitted text is kept exactly as left.
  const [completedApply] = useState(() => {
    const m = stopState('catalogExplore')
    return m.searchInput !== m.search
  })
  const [searchInput, setSearchInput] = useState(() => stopState('catalogExplore').searchInput)
  const [search, setSearch] = useState(() => stopState('catalogExplore').searchInput)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [targetPages] = useState(() => stopState('catalogExplore').pageCount)

  const decksQuery = usePublicDecksInfinite(20, search || undefined, true)
  const copyDeck   = useCopyDeck()

  const decks  = decksQuery.data?.pages.flatMap((p) => p.items) ?? []
  const total  = decksQuery.data?.pages[0]?.total ?? 0
  const loadedPages = decksQuery.data?.pages.length ?? 0

  useEffect(() => {
    patchStopState('catalogExplore', { searchInput, search })
  }, [searchInput, search])
  useEffect(() => {
    if (loadedPages > 0) patchStopState('catalogExplore', { pageCount: loadedPages })
  }, [loadedPages])

  // Coming back: load the pages that were loaded before (the query cache may
  // have let them go), one at a time, while more exist. If the list has fewer
  // now, it simply stops — the list got shorter, and the offset clamps. Only
  // for the remembered list: once reached, or once the search changes, it is
  // done, and "load more" is the reader's again.
  const { hasNextPage, isFetchingNextPage, fetchNextPage, isLoading: exploreLoading } = decksQuery
  const restoringExtent = useRef(!completedApply)
  const restoredSearch = useRef(search)
  if (search !== restoredSearch.current) restoringExtent.current = false
  const extentReached = !restoringExtent.current || loadedPages >= targetPages || !hasNextPage
  useEffect(() => {
    if (exploreLoading || isFetchingNextPage) return
    if (extentReached) { restoringExtent.current = false; return }
    void fetchNextPage()
  }, [exploreLoading, extentReached, isFetchingNextPage, fetchNextPage])
  useStopScroll('catalog-explore', {
    ready: !exploreLoading && decksQuery.data !== undefined && extentReached && !isFetchingNextPage,
    restore: !completedApply,
  })

  function handleSearchChange(value: string) {
    setSearchInput(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setSearch(value), 400)
  }

  function handleAdd(deck: PublicDeck) {
    copyDeck.mutate({ deckId: deck.id, isActiveForLearning: false })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Search field — debounced, no separate button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 400 }}>
          <input
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={cat.searchCommunity}
            style={{
              width: '100%',
              padding: '9px 36px 9px 14px',
              fontSize: 14,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-pill)',
              color: 'var(--text-primary)',
              outline: 'none',
              boxSizing: 'border-box',
              fontFamily: 'var(--font-ui)',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
            onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
          />
          {searchInput && (
            <button
              onClick={() => { handleSearchChange('') }}
              aria-label="Clear search"
              style={{
                position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-secondary)', fontSize: 16, padding: 2, lineHeight: 1,
              }}
            >
              ×
            </button>
          )}
        </div>
        {!decksQuery.isLoading && (
          <span style={{ fontSize: 13, color: 'var(--text-secondary)', flexShrink: 0 }}>
            {lb.deckCount(total)}
          </span>
        )}
      </div>

      {/* Grid */}
      {decksQuery.isLoading ? (
        <DeckGrid>
          {Array.from({ length: 6 }).map((_, i) => <DeckCardSkeleton key={i} />)}
        </DeckGrid>
      ) : decks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
          <p style={{ fontSize: 15, margin: 0 }}>
            {search ? cat.noMatchCommunity(search) : cat.noCommunity}
          </p>
        </div>
      ) : (
        <DeckGrid>
          {decks.map((deck) => (
            <CommunityDeckCard
              key={deck.id}
              deck={deck}
              onAdd={handleAdd}
              isAdding={copyDeck.isPending && !deck.is_copied}
              navigate={navigate}
            />
          ))}
        </DeckGrid>
      )}

      {/* Load more */}
      {decksQuery.hasNextPage && (
        <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 8 }}>
          <button
            onClick={() => decksQuery.fetchNextPage()}
            disabled={decksQuery.isFetchingNextPage}
            style={{
              padding: '10px 32px',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-pill)',
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--text-primary)',
              cursor: decksQuery.isFetchingNextPage ? 'wait' : 'pointer',
              opacity: decksQuery.isFetchingNextPage ? 0.6 : 1,
              fontFamily: 'var(--font-ui)',
              minHeight: 44,
            }}
          >
            {decksQuery.isFetchingNextPage ? '…' : cat.loadMore}
          </button>
        </div>
      )}
    </div>
  )
}

// ── Community deck card — composes from shared DeckCard ───────────────────────

interface CommunityDeckCardProps {
  deck: PublicDeck
  onAdd: (deck: PublicDeck) => void
  isAdding: boolean
  navigate: ReturnType<typeof useNavigate>
}

function CommunityDeckCard({ deck, onAdd, isAdding, navigate }: CommunityDeckCardProps) {
  const { tr } = useLocale()
  const cat = tr.catalog
  const isCopied = deck.is_copied

  const metaSlot = (
    <>
      {deck.cefr_level && (
        <>
          <span aria-hidden="true">·</span>
          <CEFRBadge level={deck.cefr_level} />
        </>
      )}
      <span aria-hidden="true">·</span>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)' }}>
        {deck.author_name ?? cat.anonymous}
      </span>
    </>
  )

  const belowHeaderSlot = deck.description ? (
    <p
      style={{
        margin: '8px 0 0',
        fontSize: 13,
        fontFamily: 'var(--font-ui)',
        color: 'var(--text-secondary)',
        lineHeight: 1.5,
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
      }}
    >
      {deck.description}
    </p>
  ) : undefined

  // Preview is always available — live in bottomLeftSlot
  const bottomLeftSlot = (
    <button
      onClick={(e) => { e.stopPropagation(); navigate(`/shared/deck/${deck.share_token}`) }}
      style={{
        padding: '7px 14px',
        borderRadius: 'var(--radius-pill)',
        border: '1px solid var(--border)',
        background: 'transparent',
        fontSize: 12,
        color: 'var(--text-secondary)',
        cursor: 'pointer',
        fontFamily: 'var(--font-ui)',
        fontWeight: 500,
        minHeight: 34,
      }}
    >
      {cat.previewBtn}
    </button>
  )

  return (
    <DeckCard
      deckId={deck.id}
      title={deck.title}
      cardCount={deck.card_count}
      isSystem={false}
      hoverShadow
      onClick={() => navigate(`/shared/deck/${deck.share_token}`)}
      metaSlot={metaSlot}
      belowHeaderSlot={belowHeaderSlot}
      actionLabel={isCopied ? cat.inLibrary : (isAdding ? cat.adding : cat.add)}
      onAction={isCopied
        ? () => navigate('/library?seg=mine')
        : () => onAdd(deck)
      }
      bottomLeftSlot={bottomLeftSlot}
    />
  )
}
